import {
  renderProjectOffline,
  type OfflineRenderProject,
  type VoiceType,
} from '@/hooks/useAudioEngine'
import type { Project } from '@/stores/sequencerStore'
import type { Sample } from '@/hooks/useSampleUpload'
import { useSequencerStore } from '@/stores/sequencerStore'

/**
 * Plan 2b WAV export.
 *
 * 1. Pre-decode any custom samples the project references.
 * 2. Render the project offline into a stereo AudioBuffer.
 * 3. Encode the buffer as 16-bit PCM WAV.
 * 4. Trigger a browser download.
 *
 * All client-side — no backend involvement. Free tier allowed.
 */

/** Fetch + decode a sample into an AudioBuffer. Returns null on
 *  any failure so the caller can skip it and fall back to the
 *  procedural voice for that pad. */
async function decodeSample(
  ctx: AudioContext,
  playUrl: string,
): Promise<AudioBuffer | null> {
  try {
    const res = await fetch(playUrl)
    if (!res.ok) return null
    const bytes = await res.arrayBuffer()
    return await ctx.decodeAudioData(bytes)
  } catch (err) {
    console.warn('exportProject: sample decode failed', err)
    return null
  }
}

/**
 * Render `project` to a WAV blob. `samples` is the caller's
 * current sample list (from `useSamples`) so we can resolve
 * `customSamples` entries to their presigned GET URLs.
 *
 * `onProgress`, when set, is invoked with a 0..1 value at each
 * stage boundary: `0.1` after pad-def scan, `0.4` after sample
 * decode, `0.7` after offline render starts, `1.0` on completion.
 * Used by the UI to paint a progress bar during the render —
 * OfflineAudioContext itself doesn't expose a per-sample
 * progress signal so stage-based updates are the best we can
 * do without a polling `AudioWorklet`.
 */
export interface RenderProgress {
  /** Progress in [0, 1]. */
  progress: number
  /** Short human-readable stage label. */
  stage: string
}

export async function renderProjectToWav(
  project: Project,
  samples: Sample[] = [],
  onProgress?: (p: RenderProgress) => void,
): Promise<Blob> {
  onProgress?.({ progress: 0, stage: 'Preparing' })
  // Collect per-pad voice overrides + sample decoding tasks from
  // `padDefs` (new in v7). A single walk produces both side
  // outputs: the `voicesByIndex` map the offline renderer
  // consumes, and the list of samples we need to fetch+decode
  // before rendering.
  const voicesByIndex: Record<number, VoiceType | null> = {}
  const sampleTasks: Array<{ padIndex: number; playUrl: string }> = []
  const instrumentTasks: Array<{
    padIndex: number
    instrumentId: string
    preset?: Record<string, number | string>
    gainDb?: number
    sliceStart?: number
    sliceLen?: number
    samplePlayUrl?: string
  }> = []
  const granularByIndex: NonNullable<OfflineRenderProject['granularByIndex']> =
    {}
  // Per-pad metadata for the offline sample dispatch — pad-level
  // default envelope + gain + resolved slice region. The offline
  // renderer's sample branch reads this to gate each hit at the
  // step boundary (ADSR + src.stop) instead of playing the whole
  // buffer over and over. Matches the live `scheduleSample` path.
  const samplesByIndex: NonNullable<OfflineRenderProject['samplesByIndex']> = {}
  if (project.padDefs) {
    const sampleById = new Map(samples.map(s => [s.id, s]))
    for (const [padIdxStr, def] of Object.entries(project.padDefs)) {
      const padIndex = Number(padIdxStr)
      if (def.kind === 'synth') {
        voicesByIndex[padIndex] = def.voice
      } else if (def.kind === 'sample' || def.kind === 'granular') {
        // Sample + granular pads skip the synth path entirely.
        // Both need the decoded AudioBuffer up front. Granular
        // pads ALSO populate `granularByIndex` so the offline
        // renderer can dispatch them through Tone.GrainPlayer
        // (PR A2) instead of the plain BufferSource fallback.
        voicesByIndex[padIndex] = null
        const s = sampleById.get(def.sampleId)
        if (s && s.playUrl) {
          sampleTasks.push({ padIndex, playUrl: s.playUrl })
        }
        if (def.kind === 'granular') {
          granularByIndex[padIndex] = {
            grainSizeMs: def.grainSizeMs,
            overlap: def.overlap,
            detuneCents: def.detuneCents,
            playbackRate: def.playbackRate,
            reverse: def.reverse,
            positionSec: def.positionSec,
            positionSpread: def.positionSpread,
            pitchSpread: def.pitchSpread,
            loop: def.loop,
            gainDb: def.gainDb,
          }
        }
        if (def.kind === 'sample') {
          // Resolve the slice region the pad is bound to (if any)
          // so the offline sample branch can pass sliceStart +
          // sliceLen to the gated helper. Without this, a pad
          // pointing at "slice 3 of a chopped break" would play
          // slice 0 → natural end in the export.
          let sliceStart: number | undefined
          let sliceLen: number | undefined
          if (def.sliceId && s?.slices) {
            const sl = s.slices.find(x => x.id === def.sliceId)
            if (sl) {
              sliceStart = sl.startSec
              sliceLen = Math.max(0, sl.endSec - sl.startSec)
            }
          }
          samplesByIndex[padIndex] = {
            envelope: def.envelope,
            gainDb: def.gainDb,
            sliceStart,
            sliceLen,
            // Default `'one-shot'` mirrors live's behaviour when
            // cell.length is unset. The offline sample branch reads
            // this to decide whether cell-length locks should gate
            // audio; one-shot pads always play to the slice/sample
            // end in exports, so chopped breaks stay intact.
            playback: def.playback ?? 'one-shot',
          }
        }
      } else if (def.kind === 'midi') {
        // MIDI-only pads contribute no audio.
        voicesByIndex[padIndex] = null
      } else if (def.kind === 'instrument') {
        // Registry-plugin pads — route through the offline plugin
        // dispatch. For sampler instruments we also need the decoded
        // sample buffer pre-loaded; synth instruments ignore it.
        voicesByIndex[padIndex] = null
        const sampleId = def.sampleId
        let samplePlayUrl: string | undefined
        if (def.instrumentId === 'sampler' && sampleId) {
          const s = sampleById.get(sampleId)
          samplePlayUrl = s?.playUrl ?? undefined
          if (samplePlayUrl) {
            sampleTasks.push({ padIndex, playUrl: samplePlayUrl })
          }
        }
        instrumentTasks.push({
          padIndex,
          instrumentId: def.instrumentId,
          preset: def.preset,
          gainDb: def.gainDb,
          sliceStart: def.sliceStart,
          sliceLen: def.sliceLen,
          samplePlayUrl,
        })
      }
    }
  }

  // Decode everything we need up front — renderProjectOffline
  // can't do async work once it starts.
  const sampleBuffers = new Map<number, AudioBuffer>()
  onProgress?.({ progress: 0.1, stage: 'Decoding samples' })
  if (sampleTasks.length > 0) {
    const AC = window.AudioContext || (window as any).webkitAudioContext
    const ctx = new AC()
    try {
      let done = 0
      await Promise.all(
        sampleTasks.map(async ({ padIndex, playUrl }) => {
          const buf = await decodeSample(ctx, playUrl)
          if (buf) sampleBuffers.set(padIndex, buf)
          done += 1
          // Ramp 0.1 → 0.4 across decode completion so users see
          // real movement on sample-heavy projects.
          onProgress?.({
            progress: 0.1 + 0.3 * (done / sampleTasks.length),
            stage: 'Decoding samples',
          })
        }),
      )
    } finally {
      await ctx.close().catch(() => {})
    }
  }

  // Pull current Scrambler params from the store so the export
  // matches what the user hears during live playback. Without
  // this the offline renderer produced a clean version of the
  // pattern even when the Scrambler was engaged, which was the
  // primary "export doesn't match playback" bug. Reading at call
  // time means the user's latest slider positions apply; stale
  // params don't leak across exports.
  const storeState = useSequencerStore.getState()
  const scramblerParams = storeState.scramblerParams
  // Pull the active container's buses + the active bank's padBus
  // map so per-bus FX chains render in the export. `project.buses`
  // doesn't exist — buses live on the container, not the project —
  // so we read via the container / bank lookup. Unknown container /
  // bank → skip bus rendering (offline falls through to
  // destination, which is the legacy behaviour).
  const container = storeState.containers[storeState.currentContainerId]
  const activeBankId = container?.activeBankId
  const activeBank = activeBankId ? container?.banks[activeBankId] : undefined
  const offlineBuses = container?.buses?.map(b => ({
    id: b.id,
    effects: b.effects,
  }))
  const offlinePadBus = activeBank?.padBus

  // Build the per-pad instrument dispatch map. `sampleBuffers` is
  // keyed by padIndex, so sampler-instrument pads pick up their
  // decoded buffer from the same map the sample branch uses.
  const instrumentsByIndex: Record<
    number,
    {
      instrumentId: string
      preset?: Record<string, number | string>
      sampleBuffer?: AudioBuffer
      sliceStart?: number
      sliceLen?: number
      gainDb?: number
    }
  > = {}
  for (const t of instrumentTasks) {
    instrumentsByIndex[t.padIndex] = {
      instrumentId: t.instrumentId,
      preset: t.preset,
      sampleBuffer: sampleBuffers.get(t.padIndex),
      sliceStart: t.sliceStart,
      sliceLen: t.sliceLen,
      gainDb: t.gainDb,
    }
  }

  // Master FX perform-bank snapshot. Read the current user-driven
  // state (filter / delay / reverb / bitcrush / stutter) at call
  // time so the export captures the perform surface as-set at
  // export time. `tapeStop` is skipped — it's a one-shot ramp with
  // no duration-long meaning. Only include entries whose state
  // is non-default so the renderer can short-circuit when no
  // performance is dialled in.
  const fxState = storeState.fxState
  const fxLevels = storeState.fxLevels
  const masterFxSnapshot: OfflineRenderProject['masterFx'] = {}
  for (const id of [
    'filter',
    'delay',
    'reverb',
    'bitcrush',
    'stutter',
  ] as const) {
    const active = fxState[id] === true
    const level = fxLevels[id] ?? 0
    if (active || level !== 0) {
      masterFxSnapshot[id] = { active, level }
    }
  }
  const masterFx =
    Object.keys(masterFxSnapshot).length > 0 ? masterFxSnapshot : undefined

  const offlineProject: OfflineRenderProject = {
    bpm: project.bpm,
    kitId: project.kitId,
    stepCount: project.stepCount,
    steps: project.steps as OfflineRenderProject['steps'],
    sampleBuffers,
    voicesByIndex,
    instrumentsByIndex:
      Object.keys(instrumentsByIndex).length > 0
        ? instrumentsByIndex
        : undefined,
    granularByIndex:
      Object.keys(granularByIndex).length > 0 ? granularByIndex : undefined,
    samplesByIndex:
      Object.keys(samplesByIndex).length > 0 ? samplesByIndex : undefined,
    scramblerParams: scramblerParams.enabled ? scramblerParams : undefined,
    buses: offlineBuses,
    padBus: offlinePadBus,
    masterFx,
  }

  onProgress?.({ progress: 0.4, stage: 'Rendering audio' })
  const buffer = await renderProjectOffline(offlineProject)
  onProgress?.({ progress: 0.9, stage: 'Encoding WAV' })
  const blob = audioBufferToWavBlob(buffer)
  onProgress?.({ progress: 1, stage: 'Done' })
  return blob
}

/**
 * Kick off a browser download for the rendered WAV. Safe to
 * call multiple times per session — each click creates a fresh
 * blob URL that we revoke after the download click is queued.
 */
export async function exportProjectToWav(
  project: Project,
  samples: Sample[] = [],
  onProgress?: (p: RenderProgress) => void,
): Promise<void> {
  const blob = await renderProjectToWav(project, samples, onProgress)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${sanitizeFileName(project.name) || 'project'}.wav`
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoke on next tick — Safari sometimes reads the URL during
  // the click handler synchronously, so immediate revoke can
  // cancel the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function sanitizeFileName(name: string): string {
  return name.replace(/[^a-z0-9_\-. ]/gi, '').trim()
}

/**
 * Encode an AudioBuffer to 16-bit PCM WAV. Supports stereo and
 * mono — any other channel count would be invalid input (the
 * offline context is created stereo in `renderProjectOffline`).
 */
export function audioBufferToWavBlob(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels
  const sampleRate = buffer.sampleRate
  const numFrames = buffer.length
  const bytesPerSample = 2
  const blockAlign = numChannels * bytesPerSample
  const byteRate = sampleRate * blockAlign
  const dataSize = numFrames * blockAlign
  const fileSize = 44 + dataSize

  const out = new ArrayBuffer(fileSize)
  const view = new DataView(out)

  // RIFF header
  writeString(view, 0, 'RIFF')
  view.setUint32(4, fileSize - 8, true)
  writeString(view, 8, 'WAVE')

  // fmt subchunk
  writeString(view, 12, 'fmt ')
  view.setUint32(16, 16, true) // PCM = 16 bytes
  view.setUint16(20, 1, true) // AudioFormat = PCM
  view.setUint16(22, numChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, byteRate, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bytesPerSample * 8, true)

  // data subchunk
  writeString(view, 36, 'data')
  view.setUint32(40, dataSize, true)

  // Interleave channels and write samples
  const channelData: Float32Array[] = []
  for (let c = 0; c < numChannels; c++) {
    channelData.push(buffer.getChannelData(c))
  }

  let offset = 44
  for (let i = 0; i < numFrames; i++) {
    for (let c = 0; c < numChannels; c++) {
      // Clamp to [-1, 1] and scale to int16 range. Soft-limit
      // could reduce clipping on hot mixes, but for a MVP export
      // the user hears what they hear in the browser.
      const sample = Math.max(-1, Math.min(1, channelData[c][i]))
      const intValue =
        sample < 0 ? Math.round(sample * 0x8000) : Math.round(sample * 0x7fff)
      view.setInt16(offset, intValue, true)
      offset += 2
    }
  }

  return new Blob([out], { type: 'audio/wav' })
}

function writeString(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i))
  }
}
